import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, brl, dt } from "./_shared";
import { Plus, Trash2, Save } from "lucide-react";

const empty = { name: "", description: "", price_cents: 0, currency: "brl", interval: "month", features: [] };

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty);
  const [editing, setEditing] = useState({}); // id -> partial

  const load = () => api.get("/admin/plans").then(r => setPlans(r.data.plans));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return alert("Nome obrigatório");
    await api.post("/admin/plans", {
      ...draft,
      price_cents: Math.round(parseFloat(String(draft.price_cents).replace(",", ".") || "0") * 100),
      features: (Array.isArray(draft.features) ? draft.features : String(draft.features).split(",").map(s=>s.trim()).filter(Boolean)),
    });
    setDraft(empty);
    setCreating(false);
    load();
  };

  const save = async (p) => {
    const upd = editing[p.id];
    if (!upd) return;
    await api.patch(`/admin/plans/${p.id}`, {
      ...upd,
      price_cents: upd.price_cents != null ? Math.round(parseFloat(String(upd.price_cents).replace(",", ".") || "0") * 100) : undefined,
    });
    setEditing(e => { const c = {...e}; delete c[p.id]; return c; });
    load();
  };

  const archive = async (p) => {
    if (!confirm(`Arquivar plano "${p.name}"?`)) return;
    await api.delete(`/admin/plans/${p.id}`);
    load();
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-plans">
      <PageHeader
        title="Planos"
        subtitle="Gerencie os planos comercializados pelo Hermes"
        right={
          <button onClick={()=>setCreating(true)}
            className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold flex items-center gap-2"
            data-testid="admin-plans-new">
            <Plus className="w-4 h-4" /> Novo plano
          </button>
        }
      />

      {creating && (
        <Card className="mb-6" title="Novo plano" testid="admin-plans-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input className="col-span-2 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
              placeholder="Nome do plano *" value={draft.name} onChange={e=>setDraft({...draft, name: e.target.value})}
              data-testid="admin-plan-name"/>
            <input className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
              placeholder="Preço (ex: 99,90)" value={draft.price_cents} onChange={e=>setDraft({...draft, price_cents: e.target.value})}
              data-testid="admin-plan-price"/>
            <select className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
              value={draft.interval} onChange={e=>setDraft({...draft, interval: e.target.value})}
              data-testid="admin-plan-interval">
              <option value="month">Mensal</option>
              <option value="year">Anual</option>
              <option value="one-time">Único</option>
            </select>
            <input className="col-span-4 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
              placeholder="Descrição" value={draft.description} onChange={e=>setDraft({...draft, description: e.target.value})}
              data-testid="admin-plan-description"/>
            <input className="col-span-4 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
              placeholder="Recursos separados por vírgula" value={Array.isArray(draft.features)?draft.features.join(", "):draft.features}
              onChange={e=>setDraft({...draft, features: e.target.value})}
              data-testid="admin-plan-features"/>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold" data-testid="admin-plan-save">Criar</button>
            <button onClick={()=>{setCreating(false); setDraft(empty);}} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm">Cancelar</button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.length === 0 && <div className="text-slate-500">Nenhum plano cadastrado.</div>}
        {plans.map(p => {
          const e = editing[p.id];
          return (
            <Card key={p.id} testid={`admin-plan-card-${p.id}`}
              title={e ? undefined : p.name}
              kicker={e ? "Editando" : p.interval}
              right={<Pill tone={p.status === "active" ? "green" : "slate"}>{p.status}</Pill>}
            >
              {e ? (
                <div className="grid gap-2">
                  <input className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                    value={e.name ?? p.name} onChange={ev=>setEditing({...editing, [p.id]: {...e, name: ev.target.value}})}/>
                  <input className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                    placeholder="Preço" defaultValue={((p.price_cents||0)/100).toFixed(2).replace(".", ",")}
                    onChange={ev=>setEditing({...editing, [p.id]: {...e, price_cents: ev.target.value}})}/>
                  <textarea className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                    defaultValue={p.description}
                    onChange={ev=>setEditing({...editing, [p.id]: {...e, description: ev.target.value}})}/>
                  <select className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                    defaultValue={p.status}
                    onChange={ev=>setEditing({...editing, [p.id]: {...e, status: ev.target.value}})}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                    <option value="archived">archived</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={()=>save(p)} className="px-3 py-1.5 rounded-lg bg-emerald-600/80 text-white text-xs flex items-center gap-1"><Save className="w-3 h-3"/>Salvar</button>
                    <button onClick={()=>setEditing(en => { const c={...en}; delete c[p.id]; return c;})} className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-serif text-[#d4a24c]">{brl(p.price_cents)}<span className="text-xs text-slate-500">/{p.interval}</span></div>
                  <p className="text-sm text-slate-400 mt-2">{p.description || "—"}</p>
                  {p.features?.length > 0 && (
                    <ul className="mt-3 text-xs text-slate-400 space-y-1">
                      {p.features.map((f,i)=><li key={i}>• {f}</li>)}
                    </ul>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Assinantes: <span className="text-slate-200">{p.subscribers ?? 0}</span></span>
                    <div className="flex gap-2">
                      <button onClick={()=>setEditing({...editing, [p.id]: {}})}
                        className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs" data-testid={`admin-plan-edit-${p.id}`}>Editar</button>
                      <button onClick={()=>archive(p)}
                        className="px-3 py-1.5 rounded-lg bg-rose-900/40 text-rose-300 text-xs flex items-center gap-1" data-testid={`admin-plan-delete-${p.id}`}><Trash2 className="w-3 h-3"/>Arquivar</button>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2">Criado {dt(p.created_at)}</div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
